import { Fragment } from 'react';

import { BrainIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

import { MemoryCard } from './MemoryCard';
import { useContactMemories } from '../hooks/useContactMemories';

interface MemoriesTabProps {
  contactId: string;
}

export const MemoriesTab = ({ contactId }: MemoriesTabProps) => {
  const { memories, status } = useContactMemories(contactId);
  const { t } = useTranslation();

  if (status === 'pending') {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }

  if (memories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-muted/50">
          <BrainIcon size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-sm font-semibold text-foreground">{t('contacts.no_memories')}</h3>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          {t('contacts.no_memories_description')}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 flex w-full flex-col gap-4 px-2">
      {memories.map((memory, index) => (
        <Fragment key={memory.memory_id}>
          <MemoryCard memory={memory} />
          {index < memories.length - 1 && <Separator />}
        </Fragment>
      ))}
    </div>
  );
};
